import React, { useRef, useEffect, useState } from "react";
import { 
  motion, 
  useMotionValue, 
  useAnimationFrame 
} from "framer-motion";
import AIProductCard from "./AIProductCard";

// API Base URL
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// --- Skeleton Component ---
const SkeletonCard = () => (
  <div className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm h-full">
    <div className="aspect-[3/4] bg-gray-200 animate-pulse relative">
       <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent translate-x-[-100%] animate-[shimmer_2s_infinite]" />
    </div>
    <div className="p-5 space-y-4">
      <div className="flex justify-between items-start gap-4">
        <div className="h-5 bg-gray-200 rounded w-2/3 animate-pulse" />
        <div className="h-5 bg-gray-200 rounded w-1/4 animate-pulse" />
      </div>
      <div className="h-3 bg-gray-100 rounded w-1/2 animate-pulse" />
      <div className="h-px bg-gray-100 w-full my-1" />
      <div className="h-12 w-full bg-gray-100 rounded-lg animate-pulse" />
    </div>
  </div>
);

export default function AIProductSlider({ productIds = [], giftIds = [] }) {
  const [width, setWidth] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const carouselRef = useRef();
  const x = useMotionValue(0);
  const directionRef = useRef(-1); // -1 = moving left, 1 = moving right
  const speed = 0.5; // Adjust speed (lower is slower)

  // Fetch products and gifts from APIs
  useEffect(() => {
    const fetchProductsAndGifts = async () => {
      if (productIds.length === 0 && giftIds.length === 0) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const allItems = [];

        // Fetch all products if we have product IDs
        if (productIds && productIds.length > 0) {
          try {
            const response = await fetch(`${API_BASE_URL}/list-products`);
            if (response.ok) {
              const data = await response.json();
              // Filter products that match our IDs
              const matchedProducts = data.products.filter(p => 
                productIds.includes(p.id)
              );
              allItems.push(...matchedProducts);
            }
          } catch (error) {
            console.error('Error fetching products:', error);
          }
        }

        // Fetch all combos if we have gift IDs
        if (giftIds && giftIds.length > 0) {
          try {
            const response = await fetch(`${API_BASE_URL}/combo/list/`);
            if (response.ok) {
              const data = await response.json();
              // Filter combos that match our IDs
              const matchedCombos = data.results.filter(c => 
                giftIds.includes(c.id)
              ).map(combo => ({
                ...combo,
                itemType: 'combo', // Flag to identify it as a combo
                name: combo.title, // Normalize field name
                prices: { default: combo.price.toString() } // Normalize price structure
              }));
              allItems.push(...matchedCombos);
            }
          } catch (error) {
            console.error('Error fetching combos:', error);
          }
        }

        console.log('Fetched items:', allItems);
        setProducts(allItems);
      } catch (error) {
        console.error('Error fetching products and gifts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductsAndGifts();
  }, [productIds, giftIds]);

  // Calculate the draggable width
  useEffect(() => {
    if (carouselRef.current && products.length > 0) {
      setWidth(carouselRef.current.scrollWidth - carouselRef.current.offsetWidth);
    }
  }, [products, isLoading]);

  // Auto-Sliding Logic (Game Loop style)
  useAnimationFrame(() => {
    // Only auto-slide if content exists, not loading, not hovering, and not dragging
    if (!isLoading && width > 0 && !isHovered && !isDragging && products.length > 0) {
      const currentX = x.get();
      
      // Calculate next position
      let nextX = currentX + (directionRef.current * speed);

      // Boundary Checks (Ping-Pong effect)
      if (nextX <= -width) {
        nextX = -width;
        directionRef.current = 1; // Switch to moving right
      } else if (nextX >= 0) {
        nextX = 0;
        directionRef.current = -1; // Switch to moving left
      }

      x.set(nextX);
    }
  });

  const skeletonArray = Array(4).fill(0);

  // Show message if no products found
  if (!isLoading && products.length === 0) {
    return (
      <div className="mt-4 text-center py-4">
        <p className="text-xs text-gray-500">No recommendations available at the moment.</p>
      </div>
    );
  }

  return (
    <div 
      className="mt-4"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.div
        ref={carouselRef}
        className={`overflow-hidden ${!isLoading ? "cursor-grab active:cursor-grabbing" : ""}`}
        whileTap={!isLoading ? { cursor: "grabbing" } : {}}
      >
        <motion.div
          style={{ x }} // Bind the motion value to the div
          drag={!isLoading && products.length > 0 ? "x" : false}
          dragConstraints={{ right: 0, left: -width }}
          dragElastic={0.1}
          
          // Interaction Handlers
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => {
            setIsDragging(false);
          }}

          className="flex gap-4 pb-4"
        >
          {isLoading
            ? skeletonArray.map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="min-w-[200px] md:min-w-[220px] h-full"
                >
                  <SkeletonCard />
                </div>
              ))
            : products.map((product) => (
                <motion.div
                  key={product._id || product.id}
                  className="min-w-[200px] md:min-w-[220px] h-full"
                >
                  <AIProductCard product={product} />
                </motion.div>
              ))}
        </motion.div>
      </motion.div>

      {!isLoading && products.length > 0 && (
        <div className="flex justify-end pr-2 mt-2">
          <motion.span 
            animate={{ opacity: isHovered ? 1 : 0.5 }}
            className="text-[10px] text-gray-400 font-medium tracking-wide transition-opacity"
          >
            {isHovered ? "Paused" : "Drag to explore"}
          </motion.span>
        </div>
      )}
    </div>
  );
}